import React, { useState, useEffect, useCallback } from 'react';
import { Shield, ShieldOff, RefreshCw, Settings, AlertTriangle, CheckCircle2, Users, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import IsolirSanitizerWidget from '../components/isolir/IsolirSanitizerWidget';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IsolirManagement = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [overdueCustomers, setOverdueCustomers] = useState([]);
  const [isolatedCustomers, setIsolatedCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState({
    grace_days: 0,
    isolir_pool_name: 'pool-isolir'
  });
  const [editSettings, setEditSettings] = useState({
    grace_days: 0,
    isolir_pool_name: 'pool-isolir'
  });
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (user) {
      if (!['super_admin', 'admin', 'tenant_admin'].includes(user.role)) {
        toast.error('Access denied');
        navigate('/app/dashboard');
      }
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, overdueRes, isolatedRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/isolir/status`, { headers }),
        axios.get(`${API_URL}/api/v2/isolir/overdue-customers`, { headers }),
        axios.get(`${API_URL}/api/v2/isolir/isolated-customers`, { headers })
      ]);

      if (statusRes.data.success) {
        setStatus(statusRes.data);
        setSettings(statusRes.data.settings || {});
        setEditSettings(statusRes.data.settings || {});
      }

      if (overdueRes.data.success) {
        setOverdueCustomers(overdueRes.data.customers || []);
      }

      if (isolatedRes.data.success) {
        setIsolatedCustomers(isolatedRes.data.customers || []);
      }
    } catch (error) {
      toast.error('Gagal memuat data isolir');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUnisolirCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Yakin ingin unisolir pelanggan ${customerName}?`)) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/v2/unisolir/${customerId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message || 'Pelanggan berhasil di-unisolir');
        fetchData();
      } else {
        toast.error(response.data.error || 'Gagal unisolir pelanggan');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Terjadi kesalahan');
    }
  };

  const handleSaveSettings = async () => {
    try {
      const response = await axios.put(`${API_URL}/api/v2/isolir/settings`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          grace_days: editSettings.grace_days,
          isolir_pool_name: editSettings.isolir_pool_name
        }
      });
      
      if (response.data.success) {
        toast.success('Settings berhasil disimpan');
        setSettings(response.data.settings);
        setShowSettings(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Gagal menyimpan settings');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6" data-testid="isolir-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-orange-600 dark:text-orange-400" />
            Monitoring Isolir
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Isolir/Unisolir otomatis saat update status pembayaran di Tagihan
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-1 sm:gap-2 h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            data-testid="settings-btn"
          >
            <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Settings</span>
          </Button>
          <Button
            variant="outline"
            onClick={fetchData}
            disabled={isLoading}
            className="flex items-center gap-1 sm:gap-2 h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Auto-Sanitizer Widget (2026-09-04) */}
      <IsolirSanitizerWidget />

      {/* Settings Panel */}
      {showSettings && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-amber-800 dark:text-amber-200">Settings Isolir</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Grace Days</label>
              <Input
                type="number"
                min="0"
                value={editSettings.grace_days}
                onChange={(e) => setEditSettings({ ...editSettings, grace_days: parseInt(e.target.value) || 0 })}
                className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                data-testid="grace-days-input"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Toleransi hari sebelum isolir (0 = langsung)</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Isolir Pool Name</label>
              <Input
                type="text"
                value={editSettings.isolir_pool_name}
                onChange={(e) => setEditSettings({ ...editSettings, isolir_pool_name: e.target.value })}
                className="mt-1 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                data-testid="pool-name-input"
              />
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Nama IP Pool di MikroTik</p>
            </div>
            <div className="flex items-end">
              <Button onClick={handleSaveSettings} className="bg-amber-600 hover:bg-amber-700">
                Simpan
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/30 dark:to-slate-800">
          <CardContent className="pt-3 sm:pt-6 px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-sm text-blue-600 dark:text-blue-400 font-medium">Total Aktif</p>
                <p className="text-xl sm:text-3xl font-bold text-blue-800 dark:text-blue-200">{status?.total_active_customers || 0}</p>
              </div>
              <Users className="w-6 h-6 sm:w-10 sm:h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/30 dark:to-slate-800">
          <CardContent className="pt-3 sm:pt-6 px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-sm text-amber-600 dark:text-amber-400 font-medium">Overdue</p>
                <p className="text-xl sm:text-3xl font-bold text-amber-800 dark:text-amber-200">{status?.overdue_not_isolated || 0}</p>
              </div>
              <AlertTriangle className="w-6 h-6 sm:w-10 sm:h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-white dark:from-red-900/30 dark:to-slate-800">
          <CardContent className="pt-3 sm:pt-6 px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-sm text-red-600 dark:text-red-400 font-medium">Di-isolir</p>
                <p className="text-xl sm:text-3xl font-bold text-red-800 dark:text-red-200">{status?.already_isolated || 0}</p>
              </div>
              <Shield className="w-6 h-6 sm:w-10 sm:h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-gradient-to-br from-slate-50 to-white dark:from-slate-700/50 dark:to-slate-800">
          <CardContent className="pt-3 sm:pt-6 px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] sm:text-sm text-slate-600 dark:text-slate-400 font-medium">Tanggal</p>
                <p className="text-sm sm:text-xl font-bold text-slate-800 dark:text-slate-100">{status?.today_wib || '-'}</p>
                <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">Grace: {settings.grace_days} hari</p>
              </div>
              <Calendar className="w-6 h-6 sm:w-10 sm:h-10 text-slate-400 dark:text-slate-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Overdue Customers - Monitoring Only */}
        <Card className="border-0 shadow-lg bg-white dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700 px-3 sm:px-6 py-3 sm:py-4">
            <CardTitle className="text-sm sm:text-lg flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
              Overdue (Belum Isolir)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
            {overdueCustomers.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>Tidak ada pelanggan overdue</p>
              </div>
            ) : (
              <div className="space-y-3">
                {overdueCustomers.map((customer) => (
                  <div key={customer.id} className="border border-amber-200 dark:border-amber-800 rounded-lg p-2 sm:p-3 bg-amber-50 dark:bg-amber-900/30">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm sm:text-base text-slate-800 dark:text-slate-100 truncate">{customer.name}</p>
                        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 truncate">{customer.pppoe_username}</p>
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                          <Badge variant="outline" className="text-[10px] sm:text-xs border-amber-400 text-amber-700 dark:text-amber-400">
                            {customer.next_due_date}
                          </Badge>
                          <Badge className="text-[10px] sm:text-xs bg-red-500 text-white">
                            {customer.days_overdue} hari
                          </Badge>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                        Edit invoice untuk isolir
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Isolated Customers - With Unisolir Button */}
        <Card className="border-0 shadow-lg bg-white dark:bg-slate-800">
          <CardHeader className="border-b border-slate-100 dark:border-slate-700">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700 dark:text-red-400">
              <Shield className="w-5 h-5" />
              Pelanggan Sedang Di-isolir
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
            {isolatedCustomers.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <ShieldOff className="w-12 h-12 mx-auto mb-2 text-slate-400 dark:text-slate-500" />
                <p>Tidak ada pelanggan yang di-isolir</p>
              </div>
            ) : (
              <div className="space-y-3">
                {isolatedCustomers.map((customer) => (
                  <div key={customer.id} className="border border-red-200 dark:border-red-800 rounded-lg p-3 bg-red-50 dark:bg-red-900/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-800 dark:text-slate-100">{customer.name}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">{customer.pppoe_username}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs border-red-400 text-red-700 dark:text-red-400">
                            Isolir
                          </Badge>
                          {customer.next_due_date && (
                            <Badge variant="outline" className="text-xs">
                              Due: {customer.next_due_date}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUnisolirCustomer(customer.id, customer.name)}
                        className="flex items-center gap-1 border-green-500 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30"
                        data-testid={`unisolir-btn-${customer.id}`}
                      >
                        <ShieldOff className="w-4 h-4" />
                        Unisolir
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30">
        <CardContent className="pt-4 pb-4">
          <p className="text-sm text-green-800 dark:text-green-200">
            <strong>Info:</strong> Isolir/Unisolir berjalan otomatis saat Anda mengubah tanggal atau status pembayaran di halaman <strong>Tagihan</strong>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default IsolirManagement;
